import type { Order, OrderItem, OrderSplitState, SplitGuest } from '@/types'
import type { computeCartTotals } from '@/lib/counterPricing'

/**
 * Split-bill allocation math (F-FE-N19).
 *
 * All arithmetic runs in INTEGER PAISA so per-guest shares always sum back
 * to the order's own numbers to the paisa — no float drift, no orphan
 * remainder. The backend stays amount-agnostic (it caps each tender at the
 * remaining balance and completes when the sum reaches the total), so this
 * module is the single place guest shares are computed; the receipts and
 * the dialog both read from it.
 */

export type CartTotals = ReturnType<typeof computeCartTotals>

export interface GuestShareLine {
  orderItemId: string
  name: string
  units: number
  /** paisa-exact share of the line's net price for this guest's units */
  amount: number
}

export interface GuestShare {
  guestNo: number
  label: string
  /** items mode: this guest's lines. Empty in even mode. */
  lines: GuestShareLine[]
  /** component shares, rupees (2dp) — each column sums to the order's own component */
  itemsSubtotal: number
  discount: number
  service: number
  tax: number
  delivery: number
  /** the guest's bill: items − discount + service + tax + delivery */
  total: number
  paid: boolean
  paidPaymentId?: string | null
  tender?: 'cash' | 'card' | 'online' | null
}

const toPaisa = (n: number): number => Math.round((n ?? 0) * 100)
const fromPaisa = (p: number): number => p / 100

/**
 * Largest-remainder allocation: split `targetPaisa` across weights so the
 * result sums to the target EXACTLY. Floor each proportional share, then
 * hand the leftover paisa to the largest fractional remainders (ties → the
 * lower index). All-zero weights fall back to an equal split.
 */
export function allocateLargestRemainder(weights: number[], targetPaisa: number): number[] {
  const n = weights.length
  if (n === 0) return []
  let w = weights
  const weightSum = w.reduce((a, b) => a + b, 0)
  if (weightSum <= 0) w = new Array(n).fill(1)
  const sum = w.reduce((a, b) => a + b, 0)

  const negative = targetPaisa < 0
  const target = Math.abs(targetPaisa)

  const raw = w.map((wi) => (target * wi) / sum)
  const floors = raw.map(Math.floor)
  let leftover = target - floors.reduce((a, b) => a + b, 0)

  const byRemainder = raw
    .map((r, i) => ({ i, frac: r - Math.floor(r) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i)
  for (let k = 0; leftover > 0; k = (k + 1) % n, leftover--) {
    floors[byRemainder[k].i] += 1
  }

  return negative ? floors.map((f) => -f) : floors
}

/** Non-voided lines that carry value — what a split divides. */
export function splittableItems(order: Order): OrderItem[] {
  return (order.items ?? []).filter((it) => it.status !== 'voided')
}

/**
 * Drop voided/unknown items and trim assigned units so they never exceed
 * the remaining line quantity. Used after a mid-split void: a qty=3 line
 * assigned 2+1 becomes qty=2, and the next PUT would otherwise 400 with
 * split_assignment_exceeds_quantity. Overflow is taken from the highest
 * guest number first (same direction as the backend unpaid-then-highest trim).
 */
export function clampSplitAssignments(
  assignments: Record<string, Record<string, number>>,
  items: OrderItem[],
  guestCount: number
): Record<string, Record<string, number>> {
  const byId = new Map(
    items.filter((it) => it.status !== 'voided').map((it) => [it.id, it])
  )
  const out: Record<string, Record<string, number>> = {}
  for (const [itemId, perGuest] of Object.entries(assignments)) {
    const item = byId.get(itemId)
    if (!item) continue
    const entries: { gn: number; u: number }[] = []
    for (const [g, u] of Object.entries(perGuest)) {
      const gn = parseInt(g, 10)
      if (Number.isFinite(gn) && gn >= 1 && gn <= guestCount && u > 0) {
        entries.push({ gn, u })
      }
    }
    entries.sort((a, b) => a.gn - b.gn)
    let total = entries.reduce((a, e) => a + e.u, 0)
    if (total > item.quantity) {
      const trimOrder = [...entries].sort((a, b) => b.gn - a.gn)
      for (const e of trimOrder) {
        if (total <= item.quantity) break
        const cut = Math.min(e.u, total - item.quantity)
        e.u -= cut
        total -= cut
      }
    }
    const cleaned: Record<string, number> = {}
    for (const e of entries) {
      if (e.u > 0) cleaned[String(e.gn)] = e.u
    }
    if (Object.keys(cleaned).length > 0) out[itemId] = cleaned
  }
  return out
}

/** Line net of its own per-line discount, in paisa. */
function lineNetPaisa(it: OrderItem): number {
  return toPaisa(it.total_price) - toPaisa(it.discount_amount ?? 0)
}

/** Units of `item` assigned to each guest (index 0 = guest 1). */
function unitsByGuest(split: OrderSplitState, itemId: string): number[] {
  const per = split.assignments?.[itemId] ?? {}
  const units = new Array<number>(split.guest_count).fill(0)
  for (const [guestKey, u] of Object.entries(per)) {
    const gn = parseInt(guestKey, 10)
    if (gn >= 1 && gn <= split.guest_count) units[gn - 1] = u
  }
  return units
}

/** Count of units not yet assigned to any guest (items mode). */
export function unassignedUnits(order: Order, split: OrderSplitState): number {
  if (split.mode !== 'items') return 0
  let missing = 0
  for (const it of splittableItems(order)) {
    const assigned = unitsByGuest(split, it.id).reduce((a, b) => a + b, 0)
    missing += Math.max(0, it.quantity - assigned)
  }
  return missing
}

/** True when every unit of every non-voided line is assigned (items mode). */
export function splitAssignmentComplete(order: Order, split: OrderSplitState): boolean {
  return split.mode !== 'items' || unassignedUnits(order, split) === 0
}

/**
 * Drop paid markers whose payment no longer exists as a completed payment
 * on the order (e.g. the payment was voided after the guest settled). The
 * guest then shows as unpaid and their share is collectable again.
 */
export function reconcilePaidGuests(order: Order, split: OrderSplitState): SplitGuest[] {
  const completed = new Set(
    (order.payments ?? []).filter((p) => p.status === 'completed').map((p) => p.id)
  )
  return split.guests.map((g) =>
    g.paid_payment_id && !completed.has(g.paid_payment_id)
      ? { ...g, paid_payment_id: null, paid_amount: null, tender: null, paid_at: null }
      : g
  )
}

/**
 * Compute each guest's bill.
 *
 * `totals` must be computeCartTotals output at the order's LOCKED intent
 * (tender_scope 'mixed' → 'cash', 'card_only' → 'card') so the shares
 * divide the same tax/service/delivery the backend persisted.
 *
 * Invariant (both modes): Σ guest totals == order.total_amount to the
 * paisa. Each component column is largest-remainder-allocated against the
 * order's own component, so the cross-sum is exact by construction.
 */
export function computeSplitShares(
  order: Order,
  totals: CartTotals,
  split: OrderSplitState
): GuestShare[] {
  const guests = reconcilePaidGuests(order, split)
  const n = split.guest_count
  const totalP = toPaisa(order.total_amount)

  if (split.mode === 'even') {
    // total/N, floor; the LAST guest absorbs the remainder.
    const base = Math.floor(totalP / n)
    return guests.map((g, i) => {
      const shareP = i === n - 1 ? totalP - base * (n - 1) : base
      return {
        guestNo: g.guest_no,
        label: g.label,
        lines: [],
        itemsSubtotal: fromPaisa(shareP),
        discount: 0,
        service: 0,
        tax: 0,
        delivery: 0,
        total: fromPaisa(shareP),
        paid: !!g.paid_payment_id,
        paidPaymentId: g.paid_payment_id,
        tender: g.tender,
      }
    })
  }

  // Items mode: per-line paisa-exact allocation by assigned units, then the
  // order-level components divided proportionally to each guest's items.
  const items = splittableItems(order)
  const guestItemsP = new Array<number>(n).fill(0)
  const guestLines: GuestShareLine[][] = Array.from({ length: n }, () => [])

  for (const it of items) {
    const units = unitsByGuest(split, it.id)
    const assigned = units.reduce((a, b) => a + b, 0)
    if (assigned === 0) continue
    // Allocate only the assigned fraction of the line's net (units may be
    // partially assigned mid-flow; unassigned units keep their value out of
    // every guest's bill until assigned).
    const netP = lineNetPaisa(it)
    const perUnit = netP / it.quantity
    const assignedP = Math.round(perUnit * assigned)
    const lineShares = allocateLargestRemainder(units, assignedP)
    const name = it.product?.name ?? 'Item'
    for (let g = 0; g < n; g++) {
      if (units[g] <= 0) continue
      guestItemsP[g] += lineShares[g]
      guestLines[g].push({
        orderItemId: it.id,
        name,
        units: units[g],
        amount: fromPaisa(lineShares[g]),
      })
    }
  }

  const discShares = allocateLargestRemainder(guestItemsP, toPaisa(order.discount_amount))
  const serviceShares = allocateLargestRemainder(guestItemsP, toPaisa(totals.service))
  const taxShares = allocateLargestRemainder(guestItemsP, toPaisa(totals.tax))
  const deliveryShares = allocateLargestRemainder(guestItemsP, toPaisa(totals.delivery))

  return guests.map((g, i) => {
    const totalGP =
      guestItemsP[i] - discShares[i] + serviceShares[i] + taxShares[i] + deliveryShares[i]
    return {
      guestNo: g.guest_no,
      label: g.label,
      lines: guestLines[i],
      itemsSubtotal: fromPaisa(guestItemsP[i]),
      discount: fromPaisa(discShares[i]),
      service: fromPaisa(serviceShares[i]),
      tax: fromPaisa(taxShares[i]),
      delivery: fromPaisa(deliveryShares[i]),
      total: fromPaisa(totalGP),
      paid: !!g.paid_payment_id,
      paidPaymentId: g.paid_payment_id,
      tender: g.tender,
    }
  })
}

/**
 * The amount to actually send for a guest's payment. Clamp to the server's
 * remaining balance, and for the FINAL unpaid guest always send exactly the
 * remainder so completion fires and no orphan paisa survives even if
 * totals drifted mid-split (item void, discount change).
 */
export function guestPaymentAmount(
  share: GuestShare,
  shares: GuestShare[],
  serverRemaining: number
): number {
  const unpaid = shares.filter((s) => !s.paid)
  const isLastUnpaid = unpaid.length === 1 && unpaid[0].guestNo === share.guestNo
  if (isLastUnpaid) return Math.round(serverRemaining * 100) / 100
  return Math.min(share.total, Math.round(serverRemaining * 100) / 100)
}
